import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { syncCart, fetchCart } from "../../services/api";

// ─── Thunks ───────────────────────────────────────────────────────────

export const fetchSyncCart = createAsyncThunk(
    "cart/fetchSyncCart",
    async (cartData, { rejectWithValue }) => {
        try {
            if (!cartData?.userId || !cartData?.items) return;
            await syncCart(cartData);
        } catch (error) {
            return rejectWithValue("Cart sync failed");
        }
    }
);

export const fetchGetCart = createAsyncThunk(
    "cart/fetchGetCart",
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchCart();
            return response.data.items || [];
        } catch (error) {
            return rejectWithValue("Failed to fetch cart");
        }
    }
);

// ─── Slice ────────────────────────────────────────────────────────────

const cartSlice = createSlice({
    name: "cart",
    initialState: {
        isOpen:  false,
        product: JSON.parse(localStorage.getItem("cart")) || [],
        loading: false,
        error:   null,
    },
    reducers: {
        openCart:  (state) => { state.isOpen = true;  },
        closeCart: (state) => { state.isOpen = false; },

        addToCart: (state, action) => {
            const item = action.payload;
            const existing = state.product.find(
                p => p.product_id === item.product_id &&
                     p.size_id   === item.size_id &&
                     p.flavour   === item.flavour
            );
            if (existing) {
                existing.qty = (existing.qty || 1) + 1;
            } else {
                state.product.push({ ...item, qty: 1 });
            }
            localStorage.setItem("cart", JSON.stringify(state.product));
        },

        removeFromCart: (state, action) => {
            const item = action.payload;
            state.product = state.product.filter(
                p => !(
                    p.product_id === item.product_id &&
                    p.size_id   === item.size_id &&
                    p.flavour   === item.flavour
                )
            );
            localStorage.setItem("cart", JSON.stringify(state.product));
        },

        increaseQty: (state, action) => {
            const item = action.payload;
            const found = state.product.find(
                p => p.product_id === item.product_id &&
                     p.size_id   === item.size_id &&
                     p.flavour   === item.flavour
            );
            if (found) found.qty = (found.qty || 1) + 1;
            localStorage.setItem("cart", JSON.stringify(state.product));
        },

        decreaseQty: (state, action) => {
            const item = action.payload;
            state.product = state.product
                .map(p =>
                    p.product_id === item.product_id &&
                    p.size_id    === item.size_id &&
                    p.flavour    === item.flavour
                        ? { ...p, qty: (p.qty || 1) - 1 }
                        : p
                )
                .filter(p => p.qty > 0);
            localStorage.setItem("cart", JSON.stringify(state.product));
        },

        clearCart: (state) => {
            state.product = [];
            state.isOpen  = false;
            localStorage.removeItem("cart");
        },
    },

    extraReducers: (builder) => {
        builder
            // ── fetchGetCart ──
            .addCase(fetchGetCart.pending, (state) => {
                state.loading = true;
                state.error   = null;
            })
            .addCase(fetchGetCart.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.length > 0) {
                    // ✅ map DB shape → cart shape
                   state.product = action.payload;
                    localStorage.setItem("cart", JSON.stringify(state.product));
                }
            })
            .addCase(fetchGetCart.rejected, (state, action) => {
                state.loading = false;
                state.error   = action.payload;
            })

            // ── fetchSyncCart ──
            .addCase(fetchSyncCart.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSyncCart.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(fetchSyncCart.rejected, (state, action) => {
                state.loading = false;
                state.error   = action.payload;
            });
    }
});

export const {
    openCart,
    closeCart,
    addToCart,
    removeFromCart,
    increaseQty,
    decreaseQty,
    clearCart,
} = cartSlice.actions;

export default cartSlice.reducer;